import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import {Router} from '@angular/router';
import {DocumentGenerationDialogComponent} from '../dialogs/document-generation-dialog/document-generation-dialog.component';
import {DocumentUploadDialogComponent} from '../dialogs/document-upload-dialog/document-upload-dialog.component';
import {Entity} from '../entity/entity';
import {DocumentService} from '../services/document-service';
import {EntityService} from '../services/entity-service';
import {OrderService} from '../services/order-service';
import {PersonLoginService} from '../services/person-login-service';
import {QuoteService} from '../services/quote-service';
import {StringUtils} from '../utils/string-utils';
import {CreditDocument, Document} from './document';
import {DocumentManagementTab} from './document-management-tab';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ScheduleService} from '../services/schedule.service';
import {Observable} from 'rxjs';
import {SelectionItem} from '../selections/selection-item';
import {ColumnSelectionDialogComponent} from '../dialogs/column-selection-dialog/column-selection-dialog.component';
import {PersonService} from '../services/person-service';
import {SelectionService} from '../services/selection-service';
import {FileUtils} from "../utils/file-utils";
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'document-management',
    templateUrl: './document-management.component.html',
    styleUrls: ['./document-management.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, MatButton, MatIcon, MatTabGroup, MatTab, DatePipe]
})
export class DocumentManagementComponent implements OnInit, OnChanges {
  readonly entityType = input.required<string>({ alias: 'entity-type' });
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  readonly tab = input<string>(undefined!);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly documentService = inject(DocumentService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  private readonly quoteService = inject(QuoteService);
  private readonly orderService = inject(OrderService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly scheduleService = inject(ScheduleService);
  private readonly personService = inject(PersonService);
  private readonly selectionService = inject(SelectionService);


  readonly entity = signal<Entity>(undefined!);
  readonly documents = signal<Document[]>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly selectedTab = signal<number>(undefined!);
  readonly creditDocuments = signal<CreditDocument[]>(undefined!);
  readonly isSchedule = signal<boolean>(undefined!);

  ngOnInit(): void {
    this.resetStatus();
    this.personLoginService.getCachedLogin();
    this.initialize();
  }

  loadSelectedTab(): void {
    this.router.navigate([], {queryParams: {tab: this.getTabName()}});
  }

  isInCreditGroup(): boolean {
    return this.personLoginService.getCachedLogin()!.groups.filter(group => 'CREDIT' === group.name).length > 0;
  }

  public backToEntity(): void {
    if (this.isSchedule()) {
      this.router.navigate(['schedules', this.entity().id.entityId]);
    } else if (this.entity().id.entityName === 'QUOTE') {
      this.router.navigate(['quotes', this.entity().id.entityId, this.entity().id.entityRevision]);
    } else {
      this.router.navigate(['orders', this.entity().id.entityId, this.entity().id.entityRevision]);
    }
  }

  public download(target: Document) {
    this.resetStatus();
    this.documentService.downloadDocument(target).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, target.fileName);
    }, error => {
      this.handleError(error);
    });
  }

  showUploadDialog(): void {
    const uploadDialog = this.dialog.open(DocumentUploadDialogComponent, {
      width: '820px',
      height: '480px',
      data: {entityKey: this.entity().id, documentType: this.getTabName()},
    });
    uploadDialog.afterClosed().subscribe(result => {
      if (result) {
        this.isBusy.set(true);
        this.loadDocuments();
      }
    });
  }

  chooseRatingScheduleColumns(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.personService.getRatingScheduleColumns().subscribe({
      next: (userColumns) => {
        const selectedColumns: string[] = [];
        for (const userColumn of userColumns) {
          selectedColumns.push(userColumn.columnName);
        }
        this.selectionService.getRatingScheduleColumns().subscribe({
          next: (allColumns: SelectionItem[]) => {
            const inputDialog = this.dialog.open(ColumnSelectionDialogComponent, {
              height: '660px',
              width: '1160px',
              data: {columnNames: allColumns, selectedColumnNames: selectedColumns},
            });
            inputDialog.afterClosed().subscribe({
              next: (columnNames: string[]) => {
                if (columnNames) {
                  this.personService.saveRatingScheduleColumns(columnNames).subscribe({
                    next: () => this.isBusy.set(false),
                    error: (error) => this.handleError(error)
                  });
                } else {
                  this.isBusy.set(false);
                }
              }, error: (error) => this.handleError(error),
            });
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  showGenerateDialog(): void {
    if (this.isSchedule()) {
      this.openGenerateDialog();
    } else if (this.entity().id.entityName === 'QUOTE') {
      this.runQuoteMultiplierAndGenerate();
    } else {
      this.runContractMultiplierAndGenerate();
    }
  }

  approveCreditDocument(document: CreditDocument) {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.documentService.approveCreditDocument(document).subscribe(() => {
      this.isBusy.set(false);
      document.status = 'Approved';
    }, error => this.handleError(error));
  }

  denyCreditDocument(document: CreditDocument) {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.documentService.denyCreditDocument(document).subscribe(() => {
      this.isBusy.set(false);
      document.status = 'Denied';
    }, error => this.handleError(error));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }

  private initialize(): void {
    const tabName = this.tab();
    if (tabName && tabName.length > 0) {
      this.selectedTab.set(+DocumentManagementTab[tabName as keyof typeof DocumentManagementTab]);
      this.readPathParameters();
    } else {
      this.router.navigate([], {queryParams: {tab: 'all'}});
    }
  }

  private readPathParameters(): void {
    {
      const entityType = this.entityType();
      const idString: string = this.entityId();
      const revisionString: string = this.entityRevision();
      if (idString && idString.length > 0 && !isNaN(+revisionString)) {
        this.isSchedule.set('SCHEDULE' === entityType);
        let entityObservable: Observable<Entity> = this.entityService.getEntity(entityType, idString, +revisionString);
        if (this.isSchedule()) {
          entityObservable = this.scheduleService.getSchedule(idString);
        }
        entityObservable.subscribe({
          next: (entity) => {
            this.entity.set(entity);
            this.loadDocuments();
          },
          error: (error) => this.handleError(error)
        });
      } else {
        this.backToMain();
      }
    }
  }

  private loadDocuments(): void {
    this.isBusy.set(true);
    switch (this.selectedTab()) {
      case DocumentManagementTab.all:
        this.documentService.getEntityDocuments(this.entity().id.entityId, this.entity().id.entityRevision).subscribe(result => {
          this.documents.set(result);
          this.isBusy.set(false);
        }, error => this.handleError(error));
        break;
      case DocumentManagementTab.credit:
        this.documentService.getEntityDocuments(this.entity().id.entityId, this.entity().id.entityRevision).subscribe(allDocs => {
          if (allDocs && allDocs.length > 0) {
            this.entityService.getCreditDocuments(this.entity().id).subscribe(creditDocs => {
              if (creditDocs && creditDocs.length > 0) {
                for (const creditDoc of creditDocs) {
                  const matchingDocs = allDocs.filter(doc => creditDoc.fileName === doc.fileName);
                  if (matchingDocs && matchingDocs.length > 0) {
                    const matchingDoc = matchingDocs[0];
                    creditDoc.documentName = matchingDoc.documentName;
                    creditDoc.path = matchingDoc.path;
                    creditDoc.uploadSource = matchingDoc.uploadSource;
                    creditDoc.userName = creditDoc.updateUser;
                    creditDoc.lastUpdate = new Date(creditDoc.updateTimestamp);
                    creditDoc.documentType = matchingDoc.documentType;
                  }
                }
                this.creditDocuments.set(creditDocs);
              }
              this.isBusy.set(false);
            }, error => this.handleError(error));
          }
          this.isBusy.set(false);
        }, error => this.handleError(error));
        break;
    }
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  private backToMain(): void {
    this.router.navigate(['main']);
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
  }

  private getTabName(): string {
    return DocumentManagementTab[this.selectedTab()];
  }

  private runQuoteMultiplierAndGenerate(): void {
    this.isBusy.set(true);
    this.quoteService.runMultiplier(this.entity()).subscribe(() => {
      this.isBusy.set(false);
      this.openGenerateDialog();
    }, () => {
      this.isBusy.set(false);
      this.openGenerateDialog();
    });
  }

  private runContractMultiplierAndGenerate(): void {
    this.isBusy.set(true);
    this.orderService.runMultiplier(this.entity().id).subscribe(() => {
      this.isBusy.set(false);
      this.openGenerateDialog();
    }, () => {
      this.isBusy.set(false);
      this.openGenerateDialog();
    });
  }

  private openGenerateDialog(): void {
    const generateDialog = this.dialog.open(DocumentGenerationDialogComponent, {
      width: '500px',
      height: '700px',
      data: {entity: this.entity(), allLines: this.entity().lines.map(line => line.lineId)},
    });
    generateDialog.afterClosed().subscribe((result: string) => {
      if (result && result.length > 0) {
        this.snackBar.open(result, 'Close', {
          panelClass: ['documentTimeoutSnackBarMessage'],
          duration: 15000,
        });
      } else {
        this.isBusy.set(true);
        this.loadDocuments();
      }
    });
  }
}
